/**
 * ENHANCED TERRAIN's visible half: **hillshading the fields**.
 *
 * ## The gap this closes
 *
 * `three/relief-ground.ts` shades the bare ground by slope, through the game's
 * own palette. The landcover plates laid on top of it — 5% of Verdun, 24% of
 * London, and most of what a pilot at low level is actually looking at — kept
 * one flat authored tone however they were draped, so a 24 km² field lying
 * across a hillside read completely flat. docs/engineering/enhanced-terrain.md
 * §7 named it as the largest remaining visual gap and roadmap G4 carries it as
 * the sibling of the night-dimming item. It is also the answer to the user's
 * 2026-08-23 report that ENHANCED TERRAIN "makes no difference at all": the
 * trees are real but 2 px at altitude, whereas this repaints every field in
 * frame.
 *
 * ## Why it is only possible now
 *
 * A shade needs a *slope*, and until `three/lattice-clip.ts` a draped plate
 * triangle straddled several ground triangles and had no single one. Cut on the
 * mesh's own cells, every plate piece lies inside exactly one ground triangle,
 * so its own geometric normal **is** that triangle's normal. The shade is then
 * the same walk the ground uses, on the same normal, with no lookup.
 *
 * ## Where the colours come from — recovered, not authored
 *
 * The landcover block 128-151 is two dozen *isolated* agricultural tones rather
 * than a run (`terrain-palette.ts` §3.3), so there is no shipped ramp to
 * transcribe and each tone has to be given one. The method is
 * `LANDCOVER_NIGHT_LADDER`'s, generalised: that table already had to darken a
 * landcover tone with no run to walk, and its answer — take a luminance factor
 * and land on the nearest real `BARON.PAL` entry — is what an artist with 256
 * colours did, measured to hold hue to 0.969-1.000. The only new part here is
 * the **ladder of factors**, which is the eleven rungs `GROUND_RELIEF_RAMP`
 * itself stands on.
 *
 * **Nothing about how far or how finely the walk goes is chosen.** The span, the
 * step count and (through `terrainShadeLevel`) the mapping from slope to step
 * all come from the ground ramp, so a field is shaded by exactly as much, in as
 * many bands, as the bare ground beside it. The centre rung is factor 1 and
 * lands on the tone itself, so an unlit face draws the authored colour byte for
 * byte — the toggle's "off is today's pixels" contract.
 *
 * Run on the ground colour, the rule lands on `GROUND_RELIEF_RAMP`; run on water
 * it returns a subset of the shipped blue run 1-7. `test/landcover-shading.test.ts`
 * pins both against the real palette.
 */

import {
  GROUND_RELIEF_RAMP,
  rampIndexAt,
  terrainShadeLevel,
  type PaletteColour,
  type TerrainRamp,
} from "./terrain-palette.ts";

/**
 * How close in hue an entry must be to join a tone's ramp, as a unit dot
 * product between the two RGB vectors.
 *
 * 0.985 is the cone §3.3 recovered the ground ramp with, reused unchanged —
 * the same figure that puts 112-116 and 119 inside `#4d5928`'s family and keeps
 * everything else out.
 */
export const RAMP_HUE_CONE = 0.985;

/** Rec. 601 luma, the measure §3.3 sorts a recovered ramp by. */
export function luminanceOf(colour: PaletteColour): number {
  return (
    0.299 * ((colour >> 16) & 0xff) + 0.587 * ((colour >> 8) & 0xff) + 0.114 * (colour & 0xff)
  );
}

function hueCosine(a: PaletteColour, b: PaletteColour): number {
  const ar = (a >> 16) & 0xff, ag = (a >> 8) & 0xff, ab = a & 0xff;
  const br = (b >> 16) & 0xff, bg = (b >> 8) & 0xff, bb = b & 0xff;
  const dot = ar * br + ag * bg + ab * bb;
  const magnitude = Math.hypot(ar, ag, ab) * Math.hypot(br, bg, bb);
  return magnitude > 0 ? dot / magnitude : 0;
}

/**
 * The luminance factor each rung of `GROUND_RELIEF_RAMP` sits at, relative to
 * its own centre. This is the ladder every other tone is walked along, so a
 * field is shaded by exactly as much, in exactly as many steps, as the bare
 * ground beside it.
 */
function groundRampLadder(): number[] {
  const centre = luminanceOf(GROUND_RELIEF_RAMP.colours[GROUND_RELIEF_RAMP.centre]!) || 1;
  return GROUND_RELIEF_RAMP.colours.map((colour) => luminanceOf(colour) / centre);
}

/**
 * How far from a rung's target luminance an entry may sit and still be taken for
 * it. Half the mean gap between `GROUND_RELIEF_RAMP`'s own rungs, so a bracket
 * cannot reach past its neighbours.
 */
function rungBracket(ladder: readonly number[]): number {
  return (ladder.at(-1)! - ladder[0]!) / ((ladder.length - 1) * 2);
}

/**
 * The entry that best matches this tone's **hue** at a given luminance.
 *
 * Nearest-in-RGB is the wrong test here and was tried first: `tone x 1.4` is not
 * a colour the palette has to contain, and the nearest entry to a point that far
 * out is picked mostly on brightness — which for `BARON.PAL`'s browns is a
 * pinkish tan (`#c38a75` sits at hue cosine 0.9919, inside the cone, and reads
 * as pink on a ploughed field). Fixing the luminance first and then choosing the
 * *closest hue at that luminance* keeps a brown brown all the way up its ramp.
 */
function bestAtLuminance(
  palette: readonly PaletteColour[],
  anchor: PaletteColour,
  target: number,
  bracket: number,
): PaletteColour | undefined {
  let best: PaletteColour | undefined;
  let bestCosine = RAMP_HUE_CONE;
  for (const entry of palette) {
    if (Math.abs(luminanceOf(entry) - target) > bracket) continue;
    const cosine = hueCosine(entry, anchor);
    if (cosine >= bestCosine) {
      bestCosine = cosine;
      best = entry;
    }
  }
  return best;
}

/**
 * Recovers one tone's shade ramp from the palette.
 *
 * **The method is `LANDCOVER_NIGHT_LADDER`'s, generalised.** That table already
 * had to make a landcover tone darker without a run to walk, and its answer —
 * multiply the tone by a luminance factor and take the nearest real `BARON.PAL`
 * entry — is what a 1990 artist with 256 colours did, is measured to hold hue to
 * 0.969-1.000, and never puts a colour on a polygon that the game does not ship.
 * The only new part is the *ladder of factors*: instead of the hour's three, it
 * is the eleven rungs `GROUND_RELIEF_RAMP` itself stands on.
 *
 * The centre rung is factor 1, whose nearest entry is the tone itself, so an
 * unlit face draws the authored colour byte for byte — the toggle's "off is
 * today's pixels" contract.
 *
 * Returns `undefined` when the palette offers the tone no company at all: a lone
 * colour cannot be walked, and inventing a neighbour for it is the one thing
 * this lane refuses to do.
 */
export function recoverRamp(palette: readonly PaletteColour[], tone: PaletteColour): TerrainRamp | undefined {
  const unique = [...new Set(palette)];
  const ladder = groundRampLadder();
  const bracket = rungBracket(ladder) * luminanceOf(tone);
  const anchor = luminanceOf(tone);
  const picked = new Set<PaletteColour>([tone]);
  for (const factor of ladder) {
    const entry = bestAtLuminance(unique, tone, anchor * factor, bracket);
    if (entry !== undefined) picked.add(entry);
  }
  const colours = [...picked].sort((a, b) => luminanceOf(a) - luminanceOf(b));
  const centre = colours.indexOf(tone);
  if (colours.length < 3) return undefined;
  return { indices: colours.map((_, i) => i), colours, centre };
}

/**
 * A shading function over a whole palette: tone plus face normal in, a real
 * `BARON.PAL` entry out. Ramps are recovered once per distinct tone and cached,
 * because a region has a dozen of them and hundreds of thousands of faces.
 */
export function createLandcoverShader(
  palette: readonly PaletteColour[],
): (tone: PaletteColour, normalX: number, normalY: number, normalZ: number) => PaletteColour {
  const ramps = new Map<PaletteColour, TerrainRamp | undefined>();
  return (tone, normalX, normalY, normalZ) => {
    if (!ramps.has(tone)) ramps.set(tone, recoverRamp(palette, tone));
    const ramp = ramps.get(tone);
    if (!ramp) return tone;
    const length = Math.hypot(normalX, normalY, normalZ) || 1;
    // `SUN_DIRECTION_WORLD` is (0, 0, -1), the same horizontal sun the ground
    // mesh and the aeroplanes are lit by, so the dot product is just -nz.
    return ramp.colours[rampIndexAt(ramp, terrainShadeLevel(-normalZ / length))]!;
  };
}
